import _isEqual from 'lodash/isEqual'
import { createSelector } from 'reselect'

import { customFormat, dateFormats, dateToTimeZone, isSameDay } from 'utils/dateUtils'
import { exactStarterPlan } from 'utils/plans'

import { PLAN_TYPES } from 'containers/App/constants'
import { selectIsPax8Hierarchy } from 'containers/App/selectors'

import { PLAN_OPTIONS, PLAN_TRIAL_OPTIONS } from './constants'

const selectLoader = (state) => state.partner.editTenant.loader
const selectError = (state) => state.partner.editTenant.error
const selectData = (state) => state.partner.editTenant.data

const selectOriginalData = (state) => state.partner.editTenant.originalData

const selectMapSelectedAddons = (state) => state.partner.editTenant.mapSelectedAddons
const selectOriginalMapSelectedAddons = (state) =>
  state.partner.editTenant.originalMapSelectedAddons

const selectRelatedAddons = createSelector(
  selectData,
  selectMapSelectedAddons,
  (data, mapSelectedAddons) => {
    const relatedAddons = data.addons?.relatedFeatures
    if (!relatedAddons) return []
    const allRelatedAddons = Object.keys(relatedAddons)
      .filter((item) => mapSelectedAddons[item])
      .map((item) => relatedAddons[item])
    const merged = [].concat.apply([], allRelatedAddons)
    return merged
  }
)

const selectShowEditPlans = (state) => {
  const originalData = selectOriginalData(state)
  return !originalData.planType || !exactStarterPlan(originalData.planType)
}

const selectPlanHasChanged = (state) => {
  const originalData = selectOriginalData(state)
  const data = selectData(state)
  return originalData.planType !== data.planType
}

const selectMailboxLimitHasChanged = (state) => {
  const originalData = selectOriginalData(state)
  const data = selectData(state)
  return data.mailboxLimit && originalData.mailboxLimit !== data.mailboxLimit
}

const selectExpirationDateHasChanged = (state) => {
  const originalData = selectOriginalData(state)
  const data = selectData(state)
  return (
    data.planExpiration &&
    !isSameDay(dateToTimeZone(originalData.planExpiration), dateToTimeZone(data.planExpiration))
  )
}

const selectTrialPlanHasChanged = (state) => {
  const originalData = selectOriginalData(state)
  const data = selectData(state)
  return originalData.trialPlanType !== data.trialPlanType
}

const selectSilentModeHasChanged = (state) => {
  const originalData = selectOriginalData(state)
  const data = selectData(state)
  return originalData.silentMode !== data.silentMode
}

const selectAllowedDomainsHasChanged = (state) => {
  const originalData = selectOriginalData(state)
  const data = selectData(state)
  return originalData.allowedDomains !== data.allowedDomains
}

const selectAddonsHaveChanged = (state) => {
  const originalAddons = selectOriginalMapSelectedAddons(state)
  const addons = selectMapSelectedAddons(state)
  return !_isEqual(originalAddons, addons)
}

const selectMailboxesEnforceLimitHasChanged = (state) => {
  const originalData = selectOriginalData(state)
  const data = selectData(state)
  return originalData.enforceMailboxesLimit !== data.enforceMailboxesLimit
}

const selectActivateAutopilotHasChanged = (state) => {
  const originalData = selectOriginalData(state)
  const data = selectData(state)

  return originalData.isAutopilotEnabled !== data.isAutopilotEnabled
}

const selectDmarcForPartnerHasChanged = (state) => {
  const originalData = selectOriginalData(state)
  const data = selectData(state)

  return originalData?.enableDmarcForPartnersProps?.value !== data?.enableDmarcForPartnersProps?.value
}

const selectFormHasChanged = (state) =>
  selectSilentModeHasChanged(state) ||
  selectAllowedDomainsHasChanged(state) ||
  selectTrialPlanHasChanged(state) ||
  selectPlanHasChanged(state) ||
  selectMailboxLimitHasChanged(state) ||
  selectExpirationDateHasChanged(state) ||
  selectAddonsHaveChanged(state) ||
  selectMailboxesEnforceLimitHasChanged(state) ||
  selectActivateAutopilotHasChanged(state) ||
  selectDmarcForPartnerHasChanged(state)

const selectChangesToDisplay = (state) => {
  const showEditPlans = selectShowEditPlans(state)
  const data = selectData(state)
  const changes = []

  if (showEditPlans && selectPlanHasChanged(state)) {
    changes.push({
      label: 'License plan type',
      value: PLAN_OPTIONS.find((e) => e.value === data.planType)?.label,
    })
  }

  if (showEditPlans && selectMailboxLimitHasChanged(state)) {
    changes.push({
      label: 'Licensed mailboxes',
      value: data.mailboxLimit,
    })
  }

  if (showEditPlans && selectExpirationDateHasChanged(state)) {
    changes.push({
      label: 'Expiration date',
      value: customFormat(data.planExpiration, dateFormats.primary),
    })
  }

  if (showEditPlans && selectTrialPlanHasChanged(state)) {
    changes.push({
      label: 'Trial plan type',
      value: PLAN_OPTIONS.find((e) => e.value === data.trialPlanType)?.label,
    })
  }

  if (selectSilentModeHasChanged(state)) {
    changes.push({
      label: 'Silent mode status',
      value: data.silentMode ? 'On' : 'Off',
    })
  }

  if (selectAllowedDomainsHasChanged(state)) {
    changes.push({
      label: 'Allowed domains',
      value: data.allowedDomains,
    })
  }

  if (selectAddonsHaveChanged(state)) {
    const addons = selectMapSelectedAddons(state)
    const originalAddons = selectOriginalMapSelectedAddons(state)

    Object.keys(addons).forEach((addon) => {
      if (addons[addon] !== originalAddons[addon]) {
        changes.push({
          label: addons[addon] ? 'Add add-on' : 'Remove add-on',
          value: addon,
        })
      }
    })
  }

  if (selectMailboxesEnforceLimitHasChanged(state)) {
    changes.push({
      label: 'Enforce mailbox limit',
      value: data.enforceMailboxesLimit ? 'On' : 'Off',
    })
  }

  if (selectActivateAutopilotHasChanged(state)) {
    changes.push({
      label: 'Autopilot',
      value: data.isAutopilotEnabled ? 'Enable' : 'Disable',
    })
  }

  if (selectDmarcForPartnerHasChanged(state)) {
    changes.push({
      label: 'Enable DMARC for Partners',
      value: data.enableDmarcForPartnersProps.value ? 'Enable' : 'Disable',
    })
  }

  return changes
}

const selectAddons = createSelector(selectData, selectPlanHasChanged, (data, planHasChanged) => {
  if (!data.addons || !data.planType) return []
  const relevantAddons = data.addons[data.planType] || {}
  const res =
    relevantAddons &&
    Object.values(relevantAddons)
      ?.filter(
        (addon) =>
          !addon.displayIfActive ||
          (addon.displayIfActive && (planHasChanged ? addon.activeByDefault : addon.currentState))
      )
      ?.sort((a, b) => b.editable - a.editable)

  return res
})

const selectPlansOptions = createSelector(
  selectOriginalData,
  selectIsPax8Hierarchy,
  (originalData, isPax8Hierarchy) => {
    if (originalData.planType !== PLAN_TYPES.CORE && !isPax8Hierarchy) {
      return PLAN_OPTIONS.filter((option) => option.value !== PLAN_TYPES.CORE)
    }

    if (isPax8Hierarchy) {
      return PLAN_OPTIONS.filter((option) => option.value !== PLAN_TYPES.IRONSCALES_PROTECT)
    }

    return PLAN_OPTIONS
  }
)

const selectTrialPlansOptions = createSelector(selectOriginalData, (originalData) => {
  if (originalData.trialPlanType !== PLAN_TYPES.IRONSCALES_PROTECT) {
    return PLAN_TRIAL_OPTIONS.filter((option) => option.value !== PLAN_TYPES.IRONSCALES_PROTECT)
  }

  return PLAN_TRIAL_OPTIONS
})

export {
  selectLoader,
  selectError,
  selectData,
  selectOriginalData,
  selectFormHasChanged,
  selectChangesToDisplay,
  selectAddons,
  selectPlansOptions,
  selectTrialPlansOptions,
  selectMapSelectedAddons,
  selectOriginalMapSelectedAddons,
  selectRelatedAddons,
}
